import { useTheme } from "@emotion/react";
import { Chart as ChartJS } from "chart.js";
import { BarTimeChartSynced } from "./bar_time_chart";
import { getBarTimeChartOptions } from "./chart_options";

export function SOCChart({ metering, multiDay = false }) {
  const theme = useTheme();

  const utctime = metering.map((el) => new Date(1000 * el.utctime));
  const soc = metering.map((el) => el.soc);
  const unusableSocSeries = metering.map((el) => el.unusable_soc);
  const peakShavingSocSeries = metering.map((el) => el.peak_shaving_soc);
  const elionSocSeries = metering.map((el) => el.elion_soc);
  const parseBoolean = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (normalized === "true") return true;
      if (normalized === "false") return false;
    }
    return false;
  };
  const peakShavingEnabledSeries = metering.map((entry) =>
    parseBoolean(entry?.peak_shaving_offtake),
  );
  const parseMeteringSocValue = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const minimumSocSeries = metering.map((entry) => parseMeteringSocValue(entry?.hard_minimum_soc));
  const maximumSocSeries = metering.map((entry) => parseMeteringSocValue(entry?.hard_maximum_soc));
  const peakShavingMinSocSeries = metering.map((entry, index) =>
    peakShavingEnabledSeries[index]
      ? parseMeteringSocValue(entry?.peak_shaving_min_soc)
      : null,
  );
  const shouldShowPeakShaving = peakShavingEnabledSeries.some(Boolean);
  const minimumSocValue = minimumSocSeries.find((value) => value !== null);
  const maximumSocValue = maximumSocSeries.find((value) => value !== null);
  const peakShavingMinSocValue = peakShavingMinSocSeries.find((value) => value !== null);
  const hasSocComponents = metering.some((el) =>
    el.unusable_soc != null ||
    el.peak_shaving_soc != null ||
    el.elion_soc != null ||
    el.peak_shaving_offtake != null,
  );
  const stackedUnusableSocSeries = hasSocComponents
    ? unusableSocSeries
    : soc.map((value) => Math.min(Math.max(Number(value) || 0, 0), minimumSocValue));
  const stackedPeakShavingSocSeries = !shouldShowPeakShaving
    ? soc.map(() => 0)
    : hasSocComponents
      ? peakShavingSocSeries.map((value, index) => peakShavingEnabledSeries[index] ? value : 0)
        : soc.map((value, index) => {
          if (!peakShavingEnabledSeries[index]) return 0;

          const currentSoc = Math.max(Number(value) || 0, 0);
          return Math.min(
            Math.max(currentSoc - minimumSocValue, 0),
            Math.max(peakShavingMinSocValue - minimumSocValue, 0),
          );
        });
  const stackedElionSocSeries = hasSocComponents
    ? elionSocSeries
    : soc.map((value, index) => {
        const currentSoc = Math.max(Number(value) || 0, 0);
        return Math.max(
          currentSoc - stackedUnusableSocSeries[index] - stackedPeakShavingSocSeries[index],
          0,
        );
      });

  const datasets = [
    {
      fill: true,
      label: "Unusable SOC",
      data: stackedUnusableSocSeries,
      borderWidth: 0,
      backgroundColor: theme.palette.imbPrice.main,
      cubicInterpolationMode: "monotone",
      legendToggle: false,
    },
    ...(shouldShowPeakShaving ? [{
      fill: true,
      label: "Peak Shaving SOC",
      data: stackedPeakShavingSocSeries,
      borderWidth: 0,
      backgroundColor: theme.palette.drawnPeak.main,
      cubicInterpolationMode: "monotone",
      legendToggle: true,
    }] : []),
    {
      fill: true,
      label: "Optimization SOC",
      data: stackedElionSocSeries,
      borderWidth: 0,
      backgroundColor: theme.palette.soc.main,
      cubicInterpolationMode: "monotone",
      legendToggle: false,
    },
  ];

  if (minimumSocSeries.some((value) => value !== null && value > 0)) {
    datasets.push({
      fill: false,
      label: "Minimum SOC",
      data: minimumSocSeries,
      borderWidth: 2,
      borderColor: "black",
      backgroundColor: "transparent",
      pointStyle: false,
      pointRadius: 0,
      pointHoverRadius: 0,
      stepped: "middle",
      type: "line",
      yAxisID: "socReference",
      order: -100,
      legendToggle: false,
    });
  }

  if (maximumSocValue !== undefined) {
    datasets.push({
      fill: false,
      label: "Maximum SOC",
      data: maximumSocSeries,
      borderWidth: 2,
      borderColor: "black",
      backgroundColor: "transparent",
      pointStyle: false,
      pointRadius: 0,
      pointHoverRadius: 0,
      stepped: "middle",
      type: "line",
      yAxisID: "socReference",
      order: -100,
      legendToggle: false,
    });
  }

  if (shouldShowPeakShaving && peakShavingMinSocSeries.some((value) => value !== null)) {
    datasets.push({
      fill: false,
      label: "Peak Shaving Min SOC",
      data: peakShavingMinSocSeries,
      borderWidth: 1,
      borderColor: theme.palette.drawnPeak.main,
      backgroundColor: "transparent",
      borderDash: [6, 6],
      pointStyle: false,
      pointRadius: 0,
      pointHoverRadius: 0,
      stepped: "middle",
      type: "line",
      yAxisID: "socReference",
      order: -100,
      legendToggle: false,
    });
  }

  const data = {
    labels: utctime,
    datasets,
  };

  const options = getBarTimeChartOptions(multiDay);
  options.scales.y.stacked = true;
  options.scales.y.min = 0;
  options.scales.y.max = 100;
  options.scales.y.title = { ...options.scales.y.title, display: true, text: "%" };
  options.scales.socReference = {
    axis: "y",
    min: 0,
    max: 100,
    stacked: false,
    display: false,
    grid: {
      display: false,
    },
  };

  options.plugins = options.plugins || {};
  options.plugins.legend = options.plugins.legend || {};
  options.plugins.legend.labels = options.plugins.legend.labels || {};

  options.plugins.legend.labels.sort = (a, b) => {
    if (a.text === "Optimization SOC") return -1;
    if (b.text === "Optimization SOC") return 1;
    return 0;
  };
  options.plugins.legend.labels.filter = (legendItem, chartData) => {
    const dataset = chartData?.datasets?.[legendItem.datasetIndex];
    return !dataset?.skipLegend;
  };
  options.plugins.legend.labels.generateLabels = (chart) => {
    const baseItems = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);

    return baseItems.map((item) => {
      const dataset = chart?.data?.datasets?.[item.datasetIndex];
      const hasDashedBorder = Array.isArray(dataset?.borderDash) && dataset.borderDash.length > 0;
      const isSocLimit = dataset?.label === "Minimum SOC" || dataset?.label === "Maximum SOC";

      if (!hasDashedBorder && !isSocLimit) {
        return item;
      }

      return {
        ...item,
        fillStyle: "#ffffff",
        strokeStyle: dataset?.borderColor ?? item.strokeStyle,
        lineWidth: dataset?.borderWidth ?? 2,
        lineDash: dataset?.borderDash,
      };
    });
  };

  options.plugins.tooltip = options.plugins.tooltip || {};
  options.plugins.tooltip.callbacks = options.plugins.tooltip.callbacks || {};
  options.plugins.tooltip.filter = (tooltipItem) => {
    const label = tooltipItem?.dataset?.label;
    const value = tooltipItem?.parsed?.y;
    return [
      "Unusable SOC",
      "Minimum SOC",
      "Maximum SOC",
      "Peak Shaving Min SOC",
    ].includes(label) && value !== null && value !== undefined;
  };
  options.plugins.tooltip.callbacks.label = (tooltipItem) => {
    const index = tooltipItem?.dataIndex ?? 0;
    const unusable = Number.isFinite(stackedUnusableSocSeries[index]) ? stackedUnusableSocSeries[index] : 0;
    const peakShaving = Number.isFinite(stackedPeakShavingSocSeries[index]) ? stackedPeakShavingSocSeries[index] : 0;
    const elion = Number.isFinite(stackedElionSocSeries[index]) ? stackedElionSocSeries[index] : 0;
    const totalSoc = unusable + peakShaving + elion;
    const minSoc = minimumSocSeries[index] !== null && Number.isFinite(Number(minimumSocSeries[index]))
      ? Number(minimumSocSeries[index])
      : null;
    const maxSoc = maximumSocSeries[index] !== null && Number.isFinite(Number(maximumSocSeries[index]))
      ? Number(maximumSocSeries[index])
      : null;

    switch (tooltipItem?.dataset?.label) {
      case "Unusable SOC":
        return `Current SOC: ${Math.round(totalSoc * 100) / 100}%`;
      case "Minimum SOC":
        return `Min SOC: ${minSoc !== null ? Math.round(minSoc * 100) / 100 : "-"}%`;
      case "Maximum SOC":
        return `Max SOC: ${maxSoc !== null ? Math.round(maxSoc * 100) / 100 : "-"}%`;
      case "Peak Shaving Min SOC":
        const peakShavingMinSocAtIndex = peakShavingMinSocSeries[index];
        return `Peak shaving min SOC: ${peakShavingMinSocAtIndex !== null ? Math.round(peakShavingMinSocAtIndex * 100) / 100 : "-"}%`;
      default:
        return null;
    }
  };

  options.plugins.tooltip.callbacks.labelColor = (tooltipItem) => {
    const label = tooltipItem?.dataset?.label;
    if (label === "Unusable SOC") {
      return {
        borderColor: theme.palette.soc.main,
        backgroundColor: theme.palette.soc.main,
        borderWidth: 2,
      };
    }
    const borderColor = label === "Peak Shaving Min SOC" ? theme.palette.drawnPeak.main : "black";

    return {
      borderColor,
      backgroundColor: "#ffffff",
      borderWidth: 3,
    };
  };
  return <BarTimeChartSynced data={data} options={options} />;
}
